<script setup lang="ts">
import { useField } from 'vee-validate';
import { computed, inject, onMounted, reactive, watch } from 'vue';

import { contains, equals } from 'lib/utils/object.util';

import type { CheckboxEmits, CheckboxProps } from './Checkbox.vue.d';
import { FieldValidation } from '../form/Form.vue.d';
import Icon from '../icon/Icon.vue';

const props = withDefaults(defineProps<CheckboxProps>(), {
  binary: true,
  selectable: true,
  inputId: 'checkbox',
  withinTable: false,
  trueValue: true,
  falseValue: false,
});

const emit = defineEmits<CheckboxEmits>();

const modelValue = defineModel<typeof props.modelValue>('modelValue');

const Preset = inject<Record<string, any>>('preset', {}).checkbox;

const field = reactive<FieldValidation<typeof props.modelValue>>({
  value: undefined,
});

onMounted(() => {
  if (props.useValidator) {
    Object.assign(field, useField(props.fieldName ?? 'checkbox'));

    if (props.value != null) field.value = props.value;
  }

  if (props.modelValue != null) field.value = props.modelValue;
});

const checked = computed(() => {
  if (props.triState) return field.value;

  return props.binary
    ? field.value === props.trueValue
    : contains(props.value, props.modelValue as object[]);
});

const partialChecked = computed(() => {
  return props.triState && modelValue.value === false;
});

const onChange = (event: Event): void => {
  if (!props.disabled && !props.readonly) {
    let newModelValue: typeof props.modelValue;

    if (props.binary && !props.triState) {
      newModelValue = checked.value ? props.falseValue : props.trueValue;
    } else if (props.triState) {
      newModelValue =
        // eslint-disable-next-line no-nested-ternary
        field.value == null ? false : field.value === false ? true : null;
    } else if (Array.isArray(props.modelValue)) {
      if (checked.value)
        newModelValue = props.modelValue.filter(
          (val) => !equals(val, props.value),
        );
      else
        newModelValue = props.modelValue
          ? [...props.modelValue, props.value]
          : [props.value];
    }

    modelValue.value = newModelValue;
    field.value = newModelValue;
    emit('change', event);
  }
};

watch(
  () => props.value,
  () => {
    field.value = props.value;
  },
);

watch(
  () => props.modelValue,
  () => {
    field.value = props.modelValue;
  },
);
</script>

<template>
  <label
    v-bind="Preset?.root"
    :class="[
      { 'gap-1': props?.label, 'pointer-events-none': !props?.selectable },
    ]"
    :data-p-disabled="disabled"
    :data-p-highlight="field.value"
    data-wv-name="checkbox"
  >
    <input
      :id="inputId"
      v-bind="{ ...Preset?.input, ...props }"
      :aria-invalid="invalid || undefined"
      :aria-label="ariaLabel"
      :aria-labelledby="ariaLabelledby"
      :checked="checked"
      :disabled="disabled"
      :input-id="inputId"
      :name="name"
      :readonly="readonly"
      :required="required"
      :style="inputStyle"
      :tabindex="tabindex"
      :value="field.value"
      @blur="emit('blur', $event)"
      @change="onChange"
      @focus="emit('focus', $event)"
      data-wv-section="checkbox-input"
      type="checkbox"
    />

    <div
      v-bind="
        Preset?.box({
          props,
          context: {
            checked,
            partialChecked,
          },
        })
      "
      data-wv-section="box"
    >
      <slot :checked="checked" name="icon">
        <Icon
          :class="[...Preset?.icon.class, { 'text-transparent': !checked }]"
          :icon="checked ? 'check' : partialChecked ? 'subtract' : null"
        />
      </slot>
    </div>

    <span v-bind="Preset.label({ props })" data-wv-section="checkbox-label">
      {{ label }}
      <Icon
        v-if="tooltip"
        :info="tooltip"
        :tooltip-pos="tooltipPos"
        v-bind="Preset.tooltipicon"
        data-wv-section="tooltipicon"
        icon="info"
      />
    </span>
  </label>
</template>
